// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Compras/Recebimentos.tsx
//
//  Iteração 7 · Fase 2 — Listagem de recebimentos de compras.
//  Exibe chave NF-e, PO vinculada, valor, status e atalhos para
//  Ajuste de Preço e Manifestação DFe.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, ChevronLeft, ChevronRight, Tag, Link2, Calculator, Send, CheckCircle2,
} from 'lucide-react';

import { useRecebimentos } from '@/features/compras/hooks/useRecebimento';
import type { RecebimentoListagem } from '@/features/compras/types';
import { useDebounce } from '@/hooks/useDebounce';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const fmtMoeda = (v: number) =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtData = (s?: string) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('pt-BR'); }
  catch { return s; }
};

const statusBadgeVariant = (s: string):
  'default' | 'secondary' | 'destructive' | 'outline' =>
  s === 'TotalmenteRecebido' ? 'default'
  : s === 'ParcialmenteRecebido' ? 'secondary'
  : s === 'Cancelado' ? 'destructive'
  : 'outline';

const Recebimentos = () => {
  const navigate = useNavigate();
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const tamanhoPagina = 20;
  // Busca, período e ordem vão ao servidor: a lista inteira cresce sem limite com o tempo.
  const buscaEnviada = useDebounce(busca.trim(), 400);
  const periodo   = usePeriodo('30dias', () => setPagina(1));
  const ordenacao = useOrdenacao<'data' | 'numero' | 'valor'>('data', true, () => setPagina(1));

  const { data, isLoading, isFetching } = useRecebimentos(pagina, tamanhoPagina, {
    busca: buscaEnviada, ...periodo.intervalo,
    ordenarPor: ordenacao.ordenarPor, decrescente: ordenacao.decrescente,
  });
  const itens = data?.itens ?? [];
  const total = data?.total ?? 0;
  const totalPaginas = data?.totalPaginas ?? 1;

  const first = total === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const last  = Math.min(pagina * tamanhoPagina, total);

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Recebimentos de Compras</h1>
          <p className="text-muted-foreground mt-1">
            Entradas fiscais confirmadas. Use "Importar NF-e" para registrar uma nova.
          </p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/compras/recebimentos/importar')}>
          <Plus className="h-4 w-4" /> Importar NF-e
        </Button>
      </div>

      {/* Filtro */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="h-4 w-4" /> Buscar
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
          <Input
            className="flex-1"
            value={busca}
            onChange={(e) => { setBusca(e.target.value); setPagina(1); }}
            placeholder="Chave NF-e, número da nota, nº do recebimento ou fornecedor"
          />
          <PeriodoFiltro estado={periodo} rotuloData="Recebimento" />
        </CardContent>
      </Card>

      {/* Grid */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : itens.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {periodo.periodo !== 'todos'
                ? 'Nenhum recebimento neste período — amplie o período para ver os anteriores.'
                : 'Nenhum recebimento encontrado.'}
            </p>
          ) : (
            <div className="rounded-md border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>#</CabecalhoOrdenavel>
                    <CabecalhoOrdenavel coluna="data" ordenacao={ordenacao}>Data</CabecalhoOrdenavel>
                    <TableHead>Fornecedor</TableHead>
                    <TableHead>NF</TableHead>
                    <TableHead>Chave NF-e</TableHead>
                    <TableHead>PO</TableHead>
                    <CabecalhoOrdenavel coluna="valor" ordenacao={ordenacao} className="text-right">Valor</CabecalhoOrdenavel>
                    <TableHead>Itens</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {itens.map((r: RecebimentoListagem) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-xs">{r.numero}</TableCell>
                      <TableCell className="text-xs">{fmtData(r.dtRecebimento)}</TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate" title={r.fornecedorNome ?? ''}>
                        {r.fornecedorNome ?? `#${r.fornecedorId}`}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.numeroNf ? `${r.numeroNf}/${r.serieNf ?? '—'}` : '—'}
                      </TableCell>
                      <TableCell className="font-mono text-[10px] max-w-[140px] truncate" title={r.chaveNfe ?? ''}>
                        {r.chaveNfe ?? '—'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.ordemNumero ? (
                          <Badge variant="outline" className="gap-1">
                            <Link2 className="h-3 w-3" /> #{r.ordemNumero}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">Avulso</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-semibold">R$ {fmtMoeda(r.valorTotal)}</TableCell>
                      <TableCell className="text-xs">{r.quantidadeItens}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant={statusBadgeVariant(r.status)} className="text-xs">
                            {r.status}
                          </Badge>
                          {r.isThreeWayMatch && (
                            <span className="text-[10px] text-green-600 flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3" /> 3-way match
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Ajustar preço"
                            onClick={() =>
                              navigate(`/compras/recebimentos/${r.id}/ajuste-preco`)
                            }
                          >
                            <Calculator className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Manifestação DFe"
                            disabled={!r.chaveNfe}
                            onClick={() =>
                              navigate(`/compras/recebimentos/${r.id}/manifestacao`)
                            }
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                          {r.contaPagarGeradaId && (
                            <span
                              className="text-[10px] text-muted-foreground flex items-center gap-1"
                              title="ContaPagar gerada"
                            >
                              <Tag className="h-3 w-3" /> CP #{r.contaPagarGeradaId}
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {total > 0 && (
            <div className="flex items-center justify-between mt-4 text-sm">
              <span className="text-muted-foreground">
                Mostrando {first}–{last} de {total}
                {isFetching && !isLoading && <span className="ml-2 text-xs">(atualizando...)</span>}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagina <= 1}
                  onClick={() => setPagina((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs">
                  {pagina} / {totalPaginas}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagina >= totalPaginas}
                  onClick={() => setPagina((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Recebimentos;
